import { useEffect, useMemo, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Eye, ExternalLink, Camera, UserPlus, Sprout, ShieldQuestion, ShieldCheck, FileText, Image as ImageIcon, Upload } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { authenticatedDownload } from "../lib/pdf";
import { PROPERTY_TYPES, TYPE_BY_VALUE, benchmarkFor } from "../lib/propertyTypes";
import PropertyImage from "../components/PropertyImage";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { PropertyViewDialog } from "../components/PropertyViewDialog";
import { RetakePhotoDialog } from "../components/RetakePhotoDialog";
import USDateInput from "../components/USDateInput";
import CreateTenantDialog from "../components/CreateTenantDialog";
import ConvertLandToIncomeDialog from "../components/ConvertLandToIncomeDialog";
import SetTaxInsuranceDialog from "../components/properties/SetTaxInsuranceDialog";
import BulkImportDialog from "../components/properties/BulkImportDialog";
import TaxesDueWidget from "../components/TaxesDueWidget";
import { STR_SEGMENTS, strBenchmarkFor, projectElasticity, elasticityForSegment } from "../lib/strBenchmarks";
import { formatUSD, parseUSD } from "../lib/currency";

// TYPE_BY_VALUE + benchmarkFor still used inline for cap-rate badges
void TYPE_BY_VALUE;
void benchmarkFor;

// Alphabetical by display label. Value uses snake_case for backward-compat with server Literal.
// Catalog (emoji + cap-rate benchmarks) lives in /lib/propertyTypes.js — shared by Dashboard watchlist.
const TYPES = PROPERTY_TYPES;
const TYPE_LABEL = Object.fromEntries(TYPES.map(t => [t.value, t.label]));

function money(n) {
  return "$" + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function yearsHeldExact(isoDate) {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  const yrs = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  return yrs >= 0 ? yrs : null;
}

function yearsHeld(isoDate) {
  const y = yearsHeldExact(isoDate);
  if (y == null) return null;
  if (y < 1) return Math.max(0, Math.round(y * 10) / 10);
  return Math.floor(y);
}

export default function Properties() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [photoEditorOpen, setPhotoEditorOpen] = useState(false);
  const [imgVersion, setImgVersion] = useState(0);
  const [createTenantOpen, setCreateTenantOpen] = useState(false);
  const [convertingLand, setConvertingLand] = useState(null);
  const [tiEditing, setTiEditing] = useState(null);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  // Click-to-sort state for the properties table. `key` is the column key,
  // `dir` is 'asc' | 'desc'. null key = use API order.
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  // Cycle: ascending → descending → off (back to natural order)
  const toggleSort = (key) =>
    setSort((s) =>
      s.key !== key
        ? { key, dir: "asc" }
        : s.dir === "asc"
          ? { key, dir: "desc" }
          : { key: null, dir: "asc" },
    );

  // Pre-computed accessors so the comparator stays simple and fast.
  const SORT_ACCESSORS = {
    name:         (p) => (p.name || "").toLowerCase(),
    type:         (p) => (TYPE_LABEL[p.property_type] || p.property_type || "").toLowerCase(),
    address:      (p) => (p.address || "").toLowerCase(),
    units:        (p) => Number(p.units) || 0,
    held:         (p) => yearsHeldExact(p.purchase_date) ?? -1,
    purchase:     (p) => Number(p.purchase_price) || 0,
    market:       (p) => Number(p.market_value) || 0,
    cap_rate:     (p) => (typeof p.cap_rate === "number" ? p.cap_rate : -1),
    appreciation: (p) => {
      const purch = Number(p.purchase_price) || 0;
      const mkt = Number(p.market_value) || 0;
      return purch > 0 ? ((mkt - purch) / purch) : -Infinity;
    },
  };

  const displayItems = useMemo(() => {
    if (!sort.key) return items;
    const acc = SORT_ACCESSORS[sort.key];
    if (!acc) return items;
    const sorted = [...items].sort((a, b) => {
      const av = acc(a);
      const bv = acc(b);
      if (av === bv) return 0;
      if (typeof av === "string") return av < bv ? -1 : 1;
      return av - bv;
    });
    if (sort.dir === "desc") sorted.reverse();
    return sorted;
  }, [items, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  function defaultForm() {
    return {
      name: "", address: "", property_type: "office", units: 1,
      purchase_price: 0, purchase_date: "", market_value: 0,
      annual_taxes: 0, annual_insurance: 0,
      taxes_due_date: "",
      late_fees_accrual_date: "",
      adr: "", occupancy_pct: "", expense_ratio_pct: "", str_segment: "",
      notes: "",
      lat: null, lng: null, formatted_address: null, place_id: null, address_verified: false,
    };
  }

  const load = async () => {
    try {
      const { data } = await api.get("/properties");
      setItems(data);
    } catch (error) {
      console.error("Failed to load properties", error);
    }
  };
  useEffect(() => { load(); }, []);

  // Fetch the investor list for admin users so the Create Tenant dialog can
  // assign the new tenant to a specific landlord. Skipped for non-admins —
  // they implicitly create tenants under their own account.
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/landlords")
      .then((r) => setInvestors(r.data || []))
      .catch(() => setInvestors([]));
  }, [isAdmin]);

  const openNew = () => { setEditing(null); setForm(defaultForm()); setDuplicateWarning(null); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({
    name: p.name, address: p.address, property_type: p.property_type, units: p.units,
    purchase_price: p.purchase_price, purchase_date: p.purchase_date || "",
    market_value: p.market_value,
    annual_taxes: p.annual_taxes || 0, annual_insurance: p.annual_insurance || 0,
    taxes_due_date: p.taxes_due_date || "",
    late_fees_accrual_date: p.late_fees_accrual_date || "",
    adr: p.adr ?? "",
    occupancy_pct: p.occupancy_pct ?? "",
    expense_ratio_pct: p.expense_ratio_pct ?? "",
    str_segment: p.str_segment ?? "",
    notes: p.notes || "",
    // Re-hydrate coords; treat an already-geocoded property as verified by default.
    lat: p.lat ?? null, lng: p.lng ?? null,
    formatted_address: p.formatted_address || null,
    place_id: p.place_id || null,
    address_verified: p.lat != null && p.lng != null,
  }); setDuplicateWarning(null); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        units: Number(form.units),
        purchase_price: Number(form.purchase_price),
        market_value: Number(form.market_value),
        purchase_date: form.purchase_date || null,
        taxes_due_date: form.taxes_due_date || null,
        late_fees_accrual_date: form.late_fees_accrual_date || null,
        // Hospitality fields: send as numbers when user typed, null otherwise,
        // so non-hospitality types don't pollute the doc with empty strings.
        adr: form.adr === "" || form.adr == null ? null : Number(form.adr),
        occupancy_pct: form.occupancy_pct === "" || form.occupancy_pct == null ? null : Number(form.occupancy_pct),
        expense_ratio_pct: form.expense_ratio_pct === "" || form.expense_ratio_pct == null ? null : Number(form.expense_ratio_pct),
        str_segment: form.str_segment || null,
        // Strip our UI-only `address_verified` flag before sending to the API
        address_verified: undefined,
      };
      delete payload.address_verified;
      if (editing) {
        await api.put(`/properties/${editing.id}`, payload);
        toast.success("Property updated");
      } else {
        await api.post("/properties", payload);
        toast.success("Property added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this property and its leases/expenses?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Property deleted");
    load();
  };

  const stats = useMemo(() => {
    let purchase = 0, market = 0, weightedYrs = 0, dated = 0, totalNoi = 0;
    let landCount = 0, landMarket = 0, landPurchase = 0;
    let landWeightedYrs = 0, landDated = 0;
    for (const p of items) {
      const pp = Number(p.purchase_price) || 0;
      const mv = Number(p.market_value) || 0;
      purchase += pp;
      market += mv;
      totalNoi += Number(p.noi) || 0;
      const yrs = yearsHeldExact(p.purchase_date);
      if (yrs != null && pp > 0) {
        weightedYrs += yrs * pp;
        dated += pp;
      }
      if (p.property_type === "land") {
        landCount += 1;
        landMarket += mv;
        landPurchase += pp;
        if (yrs != null && pp > 0) {
          landWeightedYrs += yrs * pp;
          landDated += pp;
        }
      }
    }
    const gain = market - purchase;
    const pct = purchase > 0 ? (gain / purchase) * 100 : null;
    const wHold = dated > 0 ? weightedYrs / dated : null;
    const portfolioCap = market > 0 ? (totalNoi / market) * 100 : null;
    const landWHold = landDated > 0 ? landWeightedYrs / landDated : null;
    return {
      purchase, market, gain, pct, wHold, totalNoi, portfolioCap,
      landCount, landMarket, landPurchase, landWHold,
    };
  }, [items]);

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
          <div>
            <div className="overline text-slate-500">Register</div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"Outfit"}}>Properties</h1>
              <span
                data-testid="properties-count-badge"
                title="Total properties currently in your portfolio"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-slate-300 bg-slate-100 text-slate-800 text-xs font-mono font-bold tracking-wider"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                {items.length} {items.length === 1 ? "PROPERTY" : "PROPERTIES"}
              </span>
            </div>
            <p className="text-sm text-slate-500 mt-1">Your full real-estate register — itemized below.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {items.length > 0 && (
              <Link
                to="/dashboard/properties/retake"
                className="btn-outline-dark inline-flex items-center gap-2 text-sm"
                data-testid="retake-batch-open"
              >
                <Camera className="w-4 h-4" /> Retake photos
              </Link>
            )}
            <button
              data-testid="create-tenant-button"
              onClick={() => setCreateTenantOpen(true)}
              className="btn-outline-dark flex items-center gap-2 text-sm"
              title="Invite a tenant without linking them to a lease yet"
            >
              <UserPlus className="w-4 h-4" /> Create Tenant
            </button>
            <button
              data-testid="download-properties-pdf"
              onClick={async () => {
                if (items.length === 0) {
                  toast.message("Add at least one property first.");
                  return;
                }
                try {
                  const today = new Date().toISOString().slice(0, 10);
                  // Honor the current click-to-sort state so the PDF mirrors
                  // exactly what the user is looking at on screen.
                  const params = sort.key
                    ? { sort_by: sort.key, sort_dir: sort.dir }
                    : { sort_by: "name", sort_dir: "asc" };
                  await authenticatedDownload(
                    "/api/properties/pdf",
                    `properties_${today}.pdf`,
                    params,
                  );
                } catch {
                  toast.error("PDF download failed");
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-sm border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 transition-colors disabled:opacity-60"
              title="Download an itemized PDF of every property in your portfolio"
              disabled={items.length === 0}
            >
              <FileText className="w-4 h-4" /> Properties PDF
            </button>
            <button
              data-testid="download-properties-photo-report-pdf"
              onClick={async () => {
                if (items.length === 0) {
                  toast.message("Add at least one property first.");
                  return;
                }
                try {
                  const today = new Date().toISOString().slice(0, 10);
                  // Same sort honoring as the tabular PDF — the photo report
                  // mirrors the on-screen sort so investors flipping through
                  // the deck see properties in a familiar order.
                  const params = sort.key
                    ? { sort_by: sort.key, sort_dir: sort.dir }
                    : { sort_by: "name", sort_dir: "asc" };
                  toast.message("Generating photo report…", { duration: 6000 });
                  await authenticatedDownload(
                    "/api/properties/photo-report/pdf",
                    `properties_photo_report_${today}.pdf`,
                    params,
                  );
                } catch {
                  toast.error("Photo report download failed");
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-sm border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors disabled:opacity-60"
              title="Download a photo-forward PDF of every property in your portfolio (one image card per property)"
              disabled={items.length === 0}
            >
              <ImageIcon className="w-4 h-4" /> Photo Report
            </button>
            <button data-testid="bulk-import-button" onClick={() => setBulkImportOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-sm border border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100 transition-colors"
              title="Bulk import multiple properties via CSV (paste or upload)">
              <Upload className="w-4 h-4" /> Bulk Import
            </button>
            <button data-testid="add-property-button" onClick={openNew} className="btn-primary-dark flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" /> Add Property
            </button>
          </div>
        </div>

        {/* Taxes due ≤30 days alert (red banner — hidden when nothing's due) */}
        <TaxesDueWidget properties={items} />

        {/* Portfolio KPIs */}
        {items.length > 0 && (
          <div className={`grid grid-cols-2 ${stats.landCount > 0 ? "md:grid-cols-3 lg:grid-cols-6" : "md:grid-cols-5"} gap-3 mb-6`} data-testid="portfolio-kpis">
            <KpiTile label="Purchase Cost" value={money(stats.purchase)} testid="kpi-purchase-cost" bgClass="bg-red-50 border-red-200" />
            <KpiTile label="Market Value" value={money(stats.market)} testid="kpi-market-value" bgClass="bg-green-50 border-green-200" />
            <KpiTile
              label="Unrealized Gain"
              value={`${stats.gain >= 0 ? "+" : "−"}${money(Math.abs(stats.gain))}`}
              sub={stats.pct != null ? `${stats.gain >= 0 ? "▲" : "▼"} ${stats.pct.toFixed(1)}%` : null}
              tone={stats.gain > 0 ? "positive" : stats.gain < 0 ? "negative" : "neutral"}
              testid="kpi-unrealized-gain"
              bgClass="bg-orange-50 border-orange-200"
            />
            <KpiTile
              label="Weighted Hold"
              value={stats.wHold != null ? `${stats.wHold.toFixed(1)} yrs` : "—"}
              sub={stats.wHold != null ? "by capital invested" : "set purchase dates"}
              testid="kpi-weighted-hold"
              bgClass="bg-slate-100 border-slate-200"
            />
            <KpiTile
              label="Portfolio Cap Rate"
              value={stats.portfolioCap != null ? `${stats.portfolioCap.toFixed(2)}%` : "—"}
              sub={stats.totalNoi != null ? `NOI ${money(stats.totalNoi)}` : "set market values"}
              tone="accent"
              testid="kpi-cap-rate"
              bgClass="bg-blue-50 border-blue-200"
            />
            {stats.landCount > 0 && (
              <KpiTile
                label="Land Bank"
                value={money(stats.landMarket)}
                sub={
                  stats.landWHold != null
                    ? `${stats.landCount} parcel${stats.landCount === 1 ? "" : "s"} · ${stats.landWHold.toFixed(1)} yr hold`
                    : `${stats.landCount} parcel${stats.landCount === 1 ? "" : "s"} · set purchase dates`
                }
                tone="land"
                testid="kpi-land-bank"
              />
            )}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]" data-testid="properties-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th className="w-12 text-right">#</Th>
                <Th className="w-24">Photo</Th>
                <SortTh sortKey="name"         sort={sort} onClick={toggleSort}>Name</SortTh>
                <SortTh sortKey="type"         sort={sort} onClick={toggleSort}>Type</SortTh>
                <SortTh sortKey="address"      sort={sort} onClick={toggleSort}>Address</SortTh>
                <SortTh sortKey="units"        sort={sort} onClick={toggleSort} className="text-right">Units</SortTh>
                <SortTh sortKey="held"         sort={sort} onClick={toggleSort} className="text-right">Held</SortTh>
                <SortTh sortKey="purchase"     sort={sort} onClick={toggleSort} className="text-right bg-green-50">Purchase Price</SortTh>
                <SortTh sortKey="market"       sort={sort} onClick={toggleSort} className="text-right">Market Value</SortTh>
                <SortTh sortKey="cap_rate"     sort={sort} onClick={toggleSort} className="text-right">Cap Rate</SortTh>
                <SortTh sortKey="appreciation" sort={sort} onClick={toggleSort} className="text-right">Appreciation</SortTh>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={12} className="text-center py-16 text-slate-400">No properties yet. Add your first one.</td></tr>
              )}
              {displayItems.map((p, idx) => {
                const purchase = Number(p.purchase_price) || 0;
                const market = Number(p.market_value) || 0;
                const gain = market - purchase;
                const gainPct = purchase > 0 ? (gain / purchase) * 100 : null;
                const yrs = yearsHeld(p.purchase_date);
                const gainColor = gain > 0 ? "text-emerald-600" : gain < 0 ? "text-red-600" : "text-slate-500";
                const cap = p.cap_rate;
                const noi = Number(p.noi) || 0;
                const typeMeta = TYPE_BY_VALUE[p.property_type];
                const bench = typeof cap === "number" ? benchmarkFor(p.property_type, cap) : { status: "unknown" };
                const benchToneCls = bench.status === "below" ? "text-red-600" : bench.status === "above" ? "text-emerald-600" : "text-cyan-700";
                const benchChip = bench.status === "below" ? "↓" : bench.status === "above" ? "↑" : "✓";
                const capTitle = `NOI $${noi.toLocaleString()} ÷ Market Value` + (bench.min != null ? ` · ${bench.label} typical ${bench.min}–${bench.max}%` : "");
                // STR benchmark — only meaningful for hotel/motel rows that have
                // both a declared segment and an ADR+occupancy operating model.
                const isHospitality = p.property_type === "hotel" || p.property_type === "motel";
                const revpar = (isHospitality && p.adr && p.occupancy_pct)
                  ? Number(p.adr) * Number(p.occupancy_pct) / 100
                  : null;
                const strBench = (isHospitality && p.str_segment && revpar != null)
                  ? strBenchmarkFor(p.str_segment, revpar)
                  : null;
                const strToneCls = strBench?.status === "below" ? "bg-red-50 text-red-700 border-red-200"
                  : strBench?.status === "above" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : strBench?.status === "in_range" ? "bg-cyan-50 text-cyan-700 border-cyan-200"
                  : "";
                const strArrow = strBench?.status === "below" ? "↓"
                  : strBench?.status === "above" ? "↑"
                  : strBench?.status === "in_range" ? "✓" : "";
                // For under-performing hotels, compute the ADR that would put
                // current occupancy on segment-pace (target = midpoint RevPAR).
                // This turns the alert into an actionable pricing hint.
                const suggestedAdr = (strBench?.status === "below" && Number(p.occupancy_pct) > 0 && strBench.mid)
                  ? Math.round(strBench.mid / (Number(p.occupancy_pct) / 100))
                  : null;
                // Elasticity-adjusted projection of the suggested ADR — answers
                // "will this actually beat my current RevPAR after demand falls?"
                // Uses the segment-specific elasticity (luxury -0.4 → economy -0.8)
                // when available so the caveat actually reflects guest behavior.
                const elast = (suggestedAdr != null)
                  ? projectElasticity(Number(p.adr), suggestedAdr, Number(p.occupancy_pct), elasticityForSegment(p.str_segment))
                  : null;
                return (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`property-row-${p.id}`}>
                  <Td className="w-12 text-right font-mono text-xs font-bold text-slate-400" data-testid={`property-index-${p.id}`}>
                    {idx + 1}
                  </Td>
                  <Td className="w-24">
                    <PropertyImage pid={p.id} size="160x100" className="w-20 h-[50px]" rounded="rounded-sm" alt={p.name} />
                  </Td>
                  <Td className="font-semibold text-slate-900">{p.name}</Td>
                  <Td>
                    <span className="inline-flex items-center gap-1.5" title={typeMeta?.capMin != null ? `Typical cap rate: ${typeMeta.capMin}–${typeMeta.capMax}%` : ""}>
                      <span className="text-base" aria-hidden="true">{typeMeta?.emoji || "📦"}</span>
                      <span className="overline text-slate-500">{TYPE_LABEL[p.property_type] || p.property_type}</span>
                    </span>
                    {strBench && (
                      <div
                        data-testid={`str-benchmark-${p.id}`}
                        className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${strToneCls}`}
                        title={`${strBench.label} segment typical RevPAR $${strBench.min}–$${strBench.max} · current $${revpar.toFixed(0)} (${strBench.gap_pct >= 0 ? "+" : ""}${strBench.gap_pct}% vs midpoint)`}
                      >
                        {strArrow} STR ${revpar.toFixed(0)}
                      </div>
                    )}
                    {suggestedAdr != null && (
                      <div
                        data-testid={`str-suggested-adr-${p.id}`}
                        className="mt-1 ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border bg-amber-50 text-amber-800 border-amber-200 text-[10px] font-mono uppercase tracking-widest"
                        title={
                          `Raise ADR to ~$${suggestedAdr} at current ${Number(p.occupancy_pct).toFixed(0)}% occupancy to hit ${strBench.label} segment midpoint RevPAR ($${Math.round(strBench.mid)}).` +
                          (elast
                            ? `\n\nDemand elasticity (e=${elast.elasticity}): expect occupancy to drop ${elast.occDropPct}% to ~${elast.projectedOcc}%, projected RevPAR $${elast.projectedRevpar.toFixed(0)} (${elast.revparGain >= 0 ? "+" : ""}$${elast.revparGain.toFixed(0)} vs current). ${elast.netPositive ? "Net positive — try it." : "Net NEGATIVE — over-priced for current demand."}`
                            : "")
                        }
                      >
                        TRY ${suggestedAdr} ADR
                      </div>
                    )}
                    {elast && (
                      <div
                        data-testid={`str-elasticity-caveat-${p.id}`}
                        className={`mt-0.5 text-[10px] italic ${elast.netPositive ? "text-slate-500" : "text-red-600 font-semibold"}`}
                      >
                        {elast.netPositive
                          ? `exp. occ ~${elast.projectedOcc.toFixed(0)}% / RevPAR $${elast.projectedRevpar.toFixed(0)}`
                          : `but elasticity warns: RevPAR may DROP to $${elast.projectedRevpar.toFixed(0)}`}
                      </div>
                    )}
                  </Td>
                  <Td className="text-slate-600">{p.address}</Td>
                  <Td className="text-right font-mono">{p.units}</Td>
                  <Td className="text-right font-mono text-xs text-slate-600" data-testid={`prop-years-held-${p.id}`} title={p.purchase_date || ""}>{yrs != null ? `${yrs} ${yrs === 1 ? "yr" : "yrs"}` : "—"}</Td>
                  <Td className="text-right font-mono bg-green-50">${purchase.toLocaleString()}</Td>
                  <Td className="text-right font-mono">${market.toLocaleString()}</Td>
                  <Td className="text-right font-mono" data-testid={`prop-cap-rate-${p.id}`} title={capTitle}>
                    {cap != null ? (
                      <div className={benchToneCls}>
                        <div className="font-bold">{cap.toFixed(2)}%</div>
                        <div className="text-[10px] uppercase tracking-widest opacity-70">
                          NOI ${noi.toLocaleString()}
                          {bench.min != null && <> · {benchChip} {bench.min}–{bench.max}%</>}
                        </div>
                      </div>
                    ) : <span className="text-slate-400">—</span>}
                  </Td>
                  <Td className="text-right font-mono" data-testid={`prop-appreciation-${p.id}`}>
                    {purchase > 0 ? (
                      <div className={`${gainColor}`}>
                        <div className="font-bold">{gain >= 0 ? "+" : "−"}${Math.abs(gain).toLocaleString()}</div>
                        <div className="text-[10px] uppercase tracking-widest opacity-70">{gain >= 0 ? "▲" : "▼"} {gainPct.toFixed(1)}%</div>
                      </div>
                    ) : <span className="text-slate-400">—</span>}
                  </Td>
                  <Td className="text-right">
                    {p.property_type === "land" && (
                      <button
                        onClick={() => setConvertingLand(p)}
                        className="text-slate-500 hover:text-emerald-600 p-1 mr-1"
                        data-testid={`convert-land-${p.id}`}
                        title="Convert to income property — graduate this parcel into a developed asset"
                      >
                        <Sprout className="w-4 h-4" />
                      </button>
                    )}
                    {(() => {
                      const taxesSet = (Number(p.annual_taxes) || 0) > 0;
                      const insSet = (Number(p.annual_insurance) || 0) > 0;
                      const fullySet = taxesSet && insSet;
                      const Icon = fullySet ? ShieldCheck : ShieldQuestion;
                      return (
                        <button
                          onClick={() => setTiEditing(p)}
                          className={`p-1 mr-1 ${fullySet ? "text-slate-500 hover:text-emerald-700" : "text-amber-600 hover:text-amber-800"}`}
                          data-testid={`set-ti-${p.id}`}
                          title={
                            fullySet
                              ? `Tax & insurance set: $${Number(p.annual_taxes).toLocaleString()} + $${Number(p.annual_insurance).toLocaleString()} / yr`
                              : "Set annual taxes & insurance — needed for the SREO coverage check"
                          }
                        >
                          <Icon className="w-4 h-4" />
                        </button>
                      );
                    })()}
                    <button onClick={() => setViewing(p)} className="text-slate-500 hover:text-slate-900 p-1" data-testid={`view-property-${p.id}`} title="View details"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => openEdit(p)} className="text-slate-500 hover:text-slate-900 p-1 ml-2" data-testid={`edit-property-${p.id}`} title="Edit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(p.id)} className="text-slate-500 hover:text-red-600 p-1 ml-2" data-testid={`delete-property-${p.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </Td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <PropertyViewDialog
          property={viewing}
          onClose={() => setViewing(null)}
          onEdit={(p) => { setViewing(null); openEdit(p); }}
        />

        <CreateTenantDialog
          open={createTenantOpen}
          onOpenChange={setCreateTenantOpen}
          isAdmin={isAdmin}
          investors={investors}
        />

        <ConvertLandToIncomeDialog
          property={convertingLand}
          onOpenChange={(v) => !v && setConvertingLand(null)}
          onConverted={() => { setConvertingLand(null); load(); }}
        />

        <SetTaxInsuranceDialog
          property={tiEditing}
          open={!!tiEditing}
          onOpenChange={(v) => !v && setTiEditing(null)}
          onSaved={() => { setTiEditing(null); load(); }}
        />

        <RetakePhotoDialog
          property={photoEditorOpen ? editing : null}
          open={photoEditorOpen}
          onClose={() => setPhotoEditorOpen(false)}
          onSaved={() => { setImgVersion((v) => v + 1); load(); }}
        />

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            className={`${form.property_type === "hotel" || form.property_type === "motel" ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto`}
          >
            <DialogHeader>
              <DialogTitle style={{fontFamily:"Outfit"}}>{editing ? "Edit property" : "Add property"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <Field label="Name"><input required className="input-clean w-full" data-testid="prop-name-input" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} /></Field>
              <Field label="Address">
                <AddressAutocomplete
                  value={form.address}
                  verified={form.address_verified}
                  dataTestId="prop-address-input"
                  required
                  onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                  onVerified={async ({ formatted_address, lat, lng, place_id }) => {
                    setForm((f) => ({
                      ...f,
                      address: formatted_address,
                      formatted_address,
                      lat, lng,
                      place_id,
                      address_verified: true,
                    }));
                    // Duplicate check — warn if an existing property shares this Google place_id
                    if (place_id) {
                      try {
                        const { data } = await api.get("/properties/check-duplicate", {
                          params: { place_id, exclude_id: editing?.id },
                        });
                        setDuplicateWarning(data.duplicate ? data.property : null);
                      } catch {
                        setDuplicateWarning(null);
                      }
                    } else {
                      setDuplicateWarning(null);
                    }
                  }}
                  onReset={() => { setForm((f) => ({
                    ...f, address_verified: false, lat: null, lng: null, formatted_address: null, place_id: null,
                  })); setDuplicateWarning(null); }}
                  placeholder="Start typing the property's address…"
                />
              </Field>
              {duplicateWarning && (
                <div className="flex items-start gap-3 rounded-sm border border-amber-300 bg-amber-50 p-3 -mt-1" data-testid="duplicate-property-warning">
                  <div className="text-amber-700 text-lg shrink-0">⚠️</div>
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-amber-900">You already have a property at this address.</div>
                    <div className="text-amber-800 text-xs mt-0.5 font-mono">
                      "{duplicateWarning.name}" — {duplicateWarning.formatted_address || duplicateWarning.address}
                    </div>
                    <button
                      type="button"
                      onClick={() => { setOpen(false); setTimeout(() => {
                        const existing = items.find((p) => p.id === duplicateWarning.id);
                        if (existing) openEdit(existing);
                      }, 150); }}
                      data-testid="edit-duplicate-property"
                      className="mt-2 text-xs font-mono uppercase tracking-widest text-amber-900 underline hover:text-amber-700"
                    >
                      Edit that property instead →
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDuplicateWarning(null)}
                    className="text-amber-500 hover:text-amber-900 shrink-0 text-xs font-mono uppercase tracking-widest"
                    data-testid="dismiss-duplicate-warning"
                    title="Dismiss — keep creating anyway"
                  >
                    Dismiss
                  </button>
                </div>
              )}
              <div data-testid="prop-image-preview">
                <div className="relative w-full bg-slate-100 rounded-sm overflow-hidden group">
                  <PropertyImage
                    address={editing ? null : form.address}
                    pid={editing ? editing.id : null}
                    size="600x240"
                    className="w-full"
                    refreshKey={imgVersion}
                  />
                  {editing && (
                    <button
                      type="button"
                      onClick={() => setPhotoEditorOpen(true)}
                      className="absolute top-2 right-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm bg-slate-900/85 hover:bg-slate-900 text-white text-xs font-mono uppercase tracking-widest backdrop-blur-sm border border-white/10 shadow-sm transition"
                      data-testid="prop-edit-adjust-photo"
                    >
                      <Camera className="w-3.5 h-3.5" />
                      Adjust photo
                    </button>
                  )}
                </div>
                <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-slate-400">
                  {editing
                    ? "Click ‘Adjust photo’ to upload a custom shot, change camera angle, or switch to aerial view."
                    : "Preview powered by Google Street View · falls back to rooftop view"}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Type">
                  <select className="input-clean w-full" data-testid="prop-type-select" value={form.property_type} onChange={(e)=>setForm({...form, property_type:e.target.value})}>
                    {TYPES.map(t => <option key={t.value} value={t.value}>{t.emoji} {t.label}{t.capMin != null ? `  ·  ${t.capMin}–${t.capMax}% cap` : ""}</option>)}
                  </select>
                </Field>
                <Field label="Units"><input type="number" className="input-clean w-full" data-testid="prop-units-input" value={form.units} onChange={(e)=>setForm({...form, units:e.target.value})} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Purchase Price"><input type="text" inputMode="decimal" className="input-clean w-full font-mono" data-testid="prop-price-input" value={formatUSD(form.purchase_price)} onChange={(e)=>setForm({...form, purchase_price:parseUSD(e.target.value)})} placeholder="$0" /></Field>
                <Field label="Purchase Date"><USDateInput data-testid="prop-purchase-date-input" value={form.purchase_date} onChange={(iso)=>setForm({...form, purchase_date:iso})} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Market Value"><input type="text" inputMode="decimal" className="input-clean w-full font-mono" data-testid="prop-value-input" value={formatUSD(form.market_value)} onChange={(e)=>setForm({...form, market_value:parseUSD(e.target.value)})} placeholder="$0" /></Field>
                <div />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Annual Taxes">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="input-clean w-full font-mono"
                    data-testid="prop-taxes-input"
                    value={formatUSD(form.annual_taxes)}
                    onChange={(e)=>setForm({...form, annual_taxes:parseUSD(e.target.value)})}
                    placeholder="$0 / year"
                  />
                </Field>
                <Field label="Annual Insurance">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="input-clean w-full font-mono"
                    data-testid="prop-insurance-input"
                    value={formatUSD(form.annual_insurance)}
                    onChange={(e)=>setForm({...form, annual_insurance:parseUSD(e.target.value)})}
                    placeholder="$0 / year"
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Next property-tax due date">
                  <USDateInput
                    data-testid="prop-taxes-due-date-input"
                    value={form.taxes_due_date}
                    onChange={(iso) => setForm({ ...form, taxes_due_date: iso })}
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    Surfaces a red "Taxes due ≤30 days" alert on your Properties dashboard.
                  </p>
                </Field>
                <Field label="Late fees accrual date">
                  <USDateInput
                    data-testid="prop-late-fees-accrual-date-input"
                    value={form.late_fees_accrual_date}
                    onChange={(iso) => setForm({ ...form, late_fees_accrual_date: iso })}
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    Date late fees begin accruing for unpaid rent on this property.
                  </p>
                </Field>
              </div>
              {/* Hospitality-only revenue model — auto-derives NOI for hotels/motels
                  which don't operate on monthly leases. RevPAR = ADR × occupancy. */}
              {(form.property_type === "hotel" || form.property_type === "motel") && (
                <div className="rounded-sm border border-amber-200 bg-amber-50/40 p-3 space-y-3" data-testid="hospitality-fields">
                  <div className="overline text-amber-800">Hospitality revenue model</div>
                  {Number(form.adr) > 0 && Number(form.occupancy_pct) > 0 && (() => {
                      const revpar = Number(form.adr) * Number(form.occupancy_pct) / 100;
                      const strLive = form.str_segment ? strBenchmarkFor(form.str_segment, revpar) : null;
                      const strCls = strLive?.status === "below" ? "text-red-700"
                        : strLive?.status === "above" ? "text-emerald-700"
                        : strLive?.status === "in_range" ? "text-cyan-700" : "text-amber-900";
                      return (
                        <div className="text-xs font-mono text-amber-900 -mt-1" data-testid="hospitality-revpar-preview">
                          RevPAR ${revpar.toFixed(2)}
                          {Number(form.units) > 0 && (
                            <> · Annual revenue ${(revpar * Number(form.units) * 365).toLocaleString(undefined, { maximumFractionDigits: 0 })}</>
                          )}
                          {strLive && strLive.status !== "unknown" && (
                            <span className={`block sm:inline sm:ml-2 mt-1 sm:mt-0 ${strCls}`} data-testid="hospitality-str-preview">
                              {strLive.status === "below" ? "↓" : strLive.status === "above" ? "↑" : "✓"} {strLive.label} ${strLive.min}–${strLive.max} ({strLive.gap_pct >= 0 ? "+" : ""}{strLive.gap_pct}%)
                              {strLive.status === "below" && Number(form.occupancy_pct) > 0 && strLive.mid && (() => {
                                const sugg = Math.round(strLive.mid / (Number(form.occupancy_pct) / 100));
                                const e = projectElasticity(Number(form.adr), sugg, Number(form.occupancy_pct), elasticityForSegment(form.str_segment));
                                return (
                                  <span className={`block sm:inline sm:ml-2 mt-1 sm:mt-0 ${e?.netPositive === false ? "text-red-700 font-semibold" : "text-amber-800"}`} data-testid="hospitality-suggested-adr-preview">
                                    → try ${sugg} ADR
                                    {e && (
                                      <span className="ml-1 italic font-normal opacity-80">
                                        ({e.netPositive ? `exp. occ ${e.projectedOcc.toFixed(0)}% / RevPAR $${e.projectedRevpar.toFixed(0)}` : `but elasticity → RevPAR $${e.projectedRevpar.toFixed(0)} ↓`})
                                      </span>
                                    )}
                                  </span>
                                );
                              })()}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Field label="ADR ($/night)">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="input-clean w-full pl-7"
                          data-testid="prop-adr-input"
                          value={form.adr}
                          onChange={(e)=>setForm({...form, adr:e.target.value})}
                          placeholder="125"
                        />
                      </div>
                    </Field>
                    <Field label="Occupancy %">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        className="input-clean w-full"
                        data-testid="prop-occupancy-input"
                        value={form.occupancy_pct}
                        onChange={(e)=>setForm({...form, occupancy_pct:e.target.value})}
                        placeholder="65"
                      />
                    </Field>
                    <Field label="Expense ratio %">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        className="input-clean w-full"
                        data-testid="prop-expense-ratio-input"
                        value={form.expense_ratio_pct}
                        onChange={(e)=>setForm({...form, expense_ratio_pct:e.target.value})}
                        placeholder="70"
                      />
                    </Field>
                    <Field label="STR segment">
                      <select
                        className="input-clean w-full"
                        data-testid="prop-str-segment-select"
                        value={form.str_segment}
                        onChange={(e)=>setForm({...form, str_segment:e.target.value})}
                      >
                        <option value="">— None —</option>
                        {STR_SEGMENTS.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label} (${s.revparMin}–${s.revparMax})
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <div className="text-[11px] text-amber-800/80">
                    Set ADR + Occupancy to auto-derive NOI (Revenue × (1 − expense ratio)).
                    Default 70% expense ratio when blank — typical for full-service hotels.
                  </div>
                </div>
              )}
              <Field label="Notes"><textarea rows={2} className="input-clean w-full" data-testid="prop-notes-input" value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} /></Field>
              <DialogFooter>
                <button type="button" onClick={()=>setOpen(false)} className="btn-outline-dark">Cancel</button>
                <button type="submit" className="btn-primary-dark" data-testid="prop-submit-button">{editing ? "Save" : "Add"}</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        <BulkImportDialog
          open={bulkImportOpen}
          onClose={() => setBulkImportOpen(false)}
          onImported={() => { load(); }}
        />
      </div>
    </DashboardShell>
  );
}

function Th({ children, className="" }) { return <th className={`px-5 py-3 overline text-slate-600 ${className}`}>{children}</th>; }
function Td({ children, className="" }) { return <td className={`px-5 py-4 ${className}`}>{children}</td>; }

function SortTh({ children, sortKey, sort, onClick, className = "" }) {
  const active = sort.key === sortKey;
  const indicator = !active ? "↕" : sort.dir === "asc" ? "↑" : "↓";
  const align = /text-right/.test(className) ? "justify-end" : "";
  return (
    <th className={`px-5 py-3 ${className}`} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        data-testid={`prop-sort-${sortKey}`}
        title={`Sort by ${typeof children === "string" ? children : sortKey}`}
        className={`overline text-slate-600 inline-flex items-center gap-1.5 hover:text-slate-900 focus:outline-none focus-visible:text-slate-900 transition-colors w-full ${align}`}
      >
        <span>{children}</span>
        <span className={`text-[10px] font-mono leading-none transition-opacity ${active ? "opacity-100 text-slate-900" : "opacity-40"}`}>
          {indicator}
        </span>
      </button>
    </th>
  );
}
function Field({ label, children }) { return <label className="block"><div className="overline text-slate-600 mb-1">{label}</div>{children}</label>; }

function KpiTile({ label, value, sub, tone, testid, bgClass = "bg-white border-slate-200" }) {
  const toneClass =
    tone === "positive" ? "text-emerald-600" :
    tone === "negative" ? "text-red-600" :
    tone === "accent"   ? "text-cyan-700" :
    tone === "land"     ? "text-emerald-700" :
    "text-slate-900";
  const subClass =
    tone === "positive" ? "text-emerald-600" :
    tone === "negative" ? "text-red-600" :
    tone === "accent"   ? "text-cyan-700" :
    tone === "land"     ? "text-emerald-700" :
    "text-slate-500";
  return (
    <div data-testid={testid} className={`${bgClass} border rounded-sm p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200`}>
      <div className="overline text-slate-500">{label}</div>
      <div className={`mt-2 text-2xl font-bold tracking-tight font-mono ${toneClass}`}>{value}</div>
      {sub && <div className={`mt-1 text-xs ${subClass} uppercase tracking-widest font-mono`}>{sub}</div>}
    </div>
  );
}

